import React, { useState, useEffect } from 'react';
import { BsRobot } from 'react-icons/bs';
import {
  FiChevronRight,
  FiArrowLeft,
  FiHome,
  FiGitCommit,
  FiInfo,
} from 'react-icons/fi';
import { RxCross2 } from 'react-icons/rx';
import toast from 'react-hot-toast';
import { ImSpinner2 } from 'react-icons/im'; // A common icon for loading spinners

import { useAgent } from '../../context/AgentProvider';
import { updateAgentSequence } from '../../services/api';
import { DataState } from '../../context/DataProvider';

// A reusable UI component to display a path array. It is self-contained and responsive.
const PathDisplay = ({ path }) => (
  <div className="flex min-h-[44px] flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-zinc-700 bg-zinc-800/50 p-3 text-sm">
    {path.map((agent, index) => (
      <React.Fragment key={index}>
        <span className="font-mono">{agent}</span>
        {index < path.length - 1 && (
          <FiChevronRight className="flex-shrink-0 text-zinc-500" />
        )}
      </React.Fragment>
    ))}
  </div>
);

// The core pathfinding algorithm, unchanged but proven correct.
const findTraversalPath = (startSequence, endSequence) => {
  if (JSON.stringify(startSequence) === JSON.stringify(endSequence)) {
    return [startSequence[startSequence.length - 1]];
  }
  let lcaIndex = -1;
  const minLength = Math.min(startSequence.length, endSequence.length);
  for (let i = 0; i < minLength; i++) {
    if (startSequence[i] === endSequence[i]) {
      lcaIndex = i;
    } else {
      break;
    }
  }
  const pathUp = startSequence.slice(lcaIndex).reverse();
  const pathDown = endSequence.slice(lcaIndex + 1);
  return [...pathUp, ...pathDown];
};

const AgentArchitectureModal = ({ isOpen, onClose }) => {
  const {
    agentSequence,
    setAgentSequence,
    agentTraversalPaths,
    setAgentTraversalPaths,
    currentTaskId,
    setTasks,
    userGoogleId: accessToken,
  } = DataState();
  const { agentTree } = useAgent();

  const [localSequence, setLocalSequence] = useState([]);
  const [traversalPath, setTraversalPath] = useState([]);

  // NEW: Local loading state for the confirmation action
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalSequence(agentSequence);
    }
  }, [isOpen, agentSequence]);

  useEffect(() => {
    if (!isOpen) return;
    const path = findTraversalPath(agentSequence, localSequence);
    setTraversalPath(path);
  }, [localSequence, agentSequence, isOpen]);

  if (!isOpen) return null;

  // UPDATED: handleConfirm is now an async function to handle the API call
  const handleConfirm = async () => {
    // Prevent function execution if button is disabled
    if (!hasPathChanged || isConfirming) return;

    setIsConfirming(true);

    try {
      // Only call the API if a task ID exists
      if (currentTaskId) {
        await updateAgentSequence(accessToken, currentTaskId, localSequence);
        setTasks((tasks) =>
          tasks.map((task) =>
            task._id === currentTaskId
              ? {
                  ...task,
                  agent_sequence: localSequence,
                  is_agent_mode: true,
                }
              : task,
          ),
        );
      }

      // Update local state only after successful API call (or if no task ID)
      if (traversalPath.length > 1) {
        setAgentTraversalPaths([...agentTraversalPaths, traversalPath]);
      }
      setAgentSequence(localSequence);

      toast.success('Agent path confirmed and saved!');
      onClose();
    } catch (error) {
      toast.error('Could not save agent path. Please try again.');
      // Do not close the modal on error, so the user can retry.
    } finally {
      // Always stop the loading state
      setIsConfirming(false);
    }
  };

  const handleCancel = () => onClose();
  const handleSelectAgent = (agentName) =>
    setLocalSequence([...localSequence, agentName]);
  const handleGoBack = () => {
    if (localSequence.length > 1) setLocalSequence(localSequence.slice(0, -1));
  };
  const handleBreadcrumbClick = (index) =>
    setLocalSequence(localSequence.slice(0, index + 1));

  const currentAgent = localSequence[localSequence.length - 1];
  const childrenAgents = agentTree[currentAgent] || [];
  const hasPathChanged =
    JSON.stringify(localSequence) !== JSON.stringify(agentSequence);
  const isActualTraversal = traversalPath.length > 1;

  return (
    // **RESPONSIVE CHANGE**: Modal container is now responsive.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      {/* Manages width and scrolling for different screen sizes */}
      <div className="animate-fadeIn flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-zinc-700 bg-zinc-900 shadow-2xl md:max-w-2xl lg:max-w-3xl">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-zinc-800 p-4">
          <div className="flex items-center gap-3">
            <BsRobot className="text-xl text-blue-400" />
            <h2 className="text-lg font-semibold">Agent Architecture</h2>
          </div>
          <button
            onClick={handleCancel}
            className="rounded-full p-1 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-white"
          >
            <RxCross2 size={20} />
          </button>
        </div>

        {/* **RESPONSIVE CHANGE**: Main content area is now scrollable on small screens. */}
        <div className="flex-grow overflow-y-auto p-4 md:p-6">
          <div className="mb-6 flex flex-wrap items-center gap-2 text-sm text-zinc-400">
            {localSequence.map((agent, index) => (
              <React.Fragment key={agent + index}>
                <button
                  onClick={() => handleBreadcrumbClick(index)}
                  className={`transition-colors hover:text-white ${index === localSequence.length - 1 ? 'font-medium text-white' : ''}`}
                >
                  {agent === 'Orchestrator' ? (
                    <FiHome className="mb-1 inline-block" />
                  ) : (
                    agent
                  )}
                </button>
                {index < localSequence.length - 1 && (
                  <FiChevronRight className="flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* **RESPONSIVE CHANGE**: This layout stacks vertically on mobile and goes horizontal on medium screens and up. */}
          <div className="flex flex-col gap-6 md:flex-row">
            <div className="flex w-full flex-shrink-0 flex-col items-center gap-3 md:w-1/3">
              <button
                onClick={handleGoBack}
                disabled={localSequence.length <= 1}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-zinc-800 px-4 py-2 font-semibold text-white transition-all hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FiArrowLeft />
                <span>Back</span>
              </button>
              <div className="w-full rounded-lg border border-[#5652CF] bg-blue-900/50 p-4 text-center">
                <BsRobot className="mx-auto mb-2 text-3xl text-blue-300" />
                <p className="text-lg font-bold">{currentAgent}</p>
                <p className="text-xs text-zinc-300">Selected Agent</p>
              </div>
            </div>

            <div className="w-full flex-grow md:w-2/3">
              <h3 className="text-md mb-3 font-semibold text-zinc-300">
                Select Next Agent:
              </h3>
              {/* **RESPONSIVE CHANGE**: Grid stacks to 1 column on small phones, 2 on larger ones. */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {childrenAgents.length > 0 ? (
                  childrenAgents.map((child) => (
                    <button
                      key={child}
                      onClick={() => handleSelectAgent(child)}
                      className="group rounded-lg border border-transparent bg-zinc-800 p-4 text-left transition-all hover:border-zinc-500 hover:bg-zinc-700"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{child}</span>
                        <FiChevronRight className="text-zinc-500 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-white" />
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="col-span-1 rounded-lg bg-zinc-800/50 p-4 text-center text-zinc-400 sm:col-span-2">
                    <p>No further agents in this path.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 border-t border-zinc-800 pt-4">
            <div className="mb-3 flex items-center gap-2 text-zinc-300">
              <FiGitCommit />
              <h3 className="text-md font-semibold">Traversal Path</h3>
            </div>
            {/* **LOGIC CHANGE**: Conditionally show the path or a "no traversal" message. */}
            {isActualTraversal ? (
              <PathDisplay path={traversalPath} />
            ) : (
              <div className="flex items-center gap-3 rounded-lg border border-zinc-700 bg-zinc-800/50 p-3 text-sm text-zinc-400">
                <FiInfo className="flex-shrink-0" />
                <span>
                  No traversal needed. The selected agent is the current active
                  agent.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* UPDATED: Footer buttons now reflect the loading state */}
        <div className="flex flex-shrink-0 items-center justify-end gap-4 rounded-b-2xl border-t border-zinc-800 bg-zinc-900/50 p-4">
          <button
            onClick={handleCancel}
            className="rounded-lg bg-zinc-700 px-5 py-2 font-semibold text-white transition-all hover:bg-zinc-600"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!hasPathChanged || isConfirming}
            className="flex w-40 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2 font-semibold text-white transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-800/50"
          >
            {isConfirming ? (
              <>
                <ImSpinner2 className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>Confirm Selection</span>
            )}
          </button>
        </div>
      </div>
      <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.2s ease-out forwards;
          }
        `}</style>
    </div>
  );
};

export default AgentArchitectureModal;
